import { SkyButton, SkyText } from "@skypointcloud/skypoint-ui";
import { useId, useState, type ReactNode } from "react";
import { Link } from "wouter";
import {
  NOTIFY_OUTCOME_LABELS,
  NOTIFY_SUBJECT_LABELS,
  type QuietRunRow,
} from "@shared/types/notifications";
import { LoadingRows } from "@/components/access/ScreenStates";
import { useQuietRuns } from "@/hooks/use-notifications";
import { useDisplayTz } from "@/hooks/use-display-tz";
import { failureMessage } from "@/lib/api";
import { ChevronDownIcon, ChevronRightIcon, RetryIcon } from "@/lib/icons";
import { quietRunConnections, quietRunWhy } from "@/lib/notifications/sentences";
import { absolutePath } from "@/lib/routes";
import { formatInstant, instantHoverTitle, zoneCaption } from "@/lib/time/displayTime";

/**
 * "Runs with no alert" (round 4 item 3), under Recent deliveries: the newest run events that made
 * no delivery row, and why. The owner watched a connection for failures and warnings, its run
 * succeeded, and no email came; Recent deliveries lists what was sent, so a run that sent nothing
 * was invisible. Closed by default and read only when opened (a disclosure: `aria-expanded` on
 * the button, the list `aria-controls` names). The table is Recent deliveries' plain table with a
 * caption, so the two read as one family.
 */

const HEADERS = ["Time", "Run", "What", "Outcome", "Why nothing was sent"] as const;

/** The Outcome cell: "Cancelled", "Recovered", the outcome's label, or nothing. */
function outcomeWords(row: QuietRunRow): string {
  if (row.cancelled) return "Cancelled";
  if (row.recovered) return "Recovered";
  return row.outcome ? NOTIFY_OUTCOME_LABELS[row.outcome] : "";
}

export function QuietRunsSection({ instanceName }: { instanceName: string }) {
  const [open, setOpen] = useState(false);
  const regionId = useId();
  const zone = useDisplayTz();
  const query = useQuietRuns(open);
  const rows = query.data;

  function renderQuietRuns(): ReactNode {
    if (!open) return null;
    if (query.isPending) {
      return <LoadingRows rows={2} columns={5} />;
    }
    if (query.isError) {
      return (
        <div
          className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3"
          data-testid="quiet-runs-error"
        >
          <SkyText size={300}>
            Runs with no alert did not load. {failureMessage(query.error, "")}
          </SkyText>
          <SkyButton
            appearance="secondary"
            size="small"
            icon={RetryIcon}
            onClick={() => void query.refetch()}
          >
            Retry
          </SkyButton>
        </div>
      );
    }
    if (!rows || rows.length === 0) {
      return (
        <SkyText size={300} color="muted" block testId="quiet-runs-empty">
          No recent run went without an alert.
        </SkyText>
      );
    }
    return (
      <div className="overflow-x-auto">
        <table
          className="w-full border-collapse bg-background text-left"
          data-testid="quiet-runs-grid"
        >
          <caption className="px-3 py-2 text-left">
            <SkyText size={200} color="muted">
              Runs with no alert for {instanceName}: each finished run that matched no rule or
              Watch, or matched one that names no one. {zoneCaption(zone)}.
            </SkyText>
          </caption>
          <thead>
            <tr>
              {HEADERS.map((header) => (
                <th
                  key={header}
                  scope="col"
                  className="whitespace-nowrap border-b border-border px-4 py-3 text-sm font-semibold text-foreground-soft"
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const connections = quietRunConnections(row);
              return (
                <tr
                  key={row.eventId}
                  className="border-b border-border hover:bg-muted/50"
                  data-testid="quiet-run-row"
                  data-reason={row.reason}
                >
                  <td className="px-3 py-2">
                    <SkyText
                      size={200}
                      font="numeric"
                      title={instantHoverTitle(row.createdAt, zone)}
                    >
                      {formatInstant(row.createdAt, zone, "minutes")}
                    </SkyText>
                  </td>
                  <td className="px-3 py-2">
                    {row.runId ? (
                      <Link
                        href={absolutePath(`/runs/${row.runId}`)}
                        className="text-sm tabular-nums underline underline-offset-2"
                      >
                        {row.runId}
                      </Link>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200} block>
                      {row.subject ? NOTIFY_SUBJECT_LABELS[row.subject] : "Run"}
                      {row.scheduleName ? `: ${row.scheduleName}` : ""}
                    </SkyText>
                    {connections ? (
                      <SkyText size={200} color="muted" block>
                        {connections}
                      </SkyText>
                    ) : null}
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200}>{outcomeWords(row)}</SkyText>
                  </td>
                  <td className="px-3 py-2">
                    <SkyText size={200} testId="quiet-run-why">
                      {quietRunWhy(row)}
                    </SkyText>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2" data-testid="quiet-runs">
      <div>
        <SkyButton
          appearance="subtle"
          size="small"
          icon={open ? ChevronDownIcon : ChevronRightIcon}
          aria-expanded={open}
          aria-controls={regionId}
          testId="quiet-runs-toggle"
          onClick={() => setOpen((was) => !was)}
        >
          {open ? "Hide runs with no alert" : "Show runs with no alert"}
        </SkyButton>
      </div>
      <div id={regionId} hidden={!open}>
        {renderQuietRuns()}
      </div>
    </div>
  );
}
